/**
 * The Edit flight's state (`spec-onboarding-persistent-scene-edit-flight`, Technical Approach): the
 * mode switcher's *Edit* reads the twin's plan, flies the live camera to Floor Plan's plan view,
 * flattens the twin, holds the landed frame and hands it to onboarding.
 *
 * `idle → reading → flying → landed → navigated`, or `cancelled` / `fallback`. The transitions are
 * pure: `useEditFlight` runs the effects each new state asks for and feeds the answers back in. Every
 * answer carries the generation it was asked under, and one from an older press is dropped
 * (Failure States, Overtaken).
 */
import type { Map as MapboxMap } from 'mapbox-gl';
import { PLAN_REST_HEIGHT_SCALE, planEase } from '../../../components/3D/planView/planMapLock';
import { resetLiveScene } from '../../../components/3D/planView/liveEditFlightScene';

/** R3.2: the flight's length. A camera duration passed to `flyToTarget`, not a UI motion token. */
export const EDIT_FLIGHT_MS = 1000;
/** Failure States: the plan read's whole budget, both reads together, from the press. */
export const EDIT_FLIGHT_PLAN_READ_TIMEOUT_MS = 1500;
/** R6.5: how long an Edit hold waits for Floor Plan's map to announce itself. */
export const EDIT_ANNOUNCE_WAIT_MS = 3000;
/** R6.5: the longest an Edit hold lasts, from the capture. */
export const EDIT_HOLD_CAP_MS = 4000;
/**
 * The longest the landing waits for the map to draw the frame it captures. A map that never draws
 * again (its WebGL context lost) lands as `capture-failed`, and Edit still navigates.
 */
export const EDIT_CAPTURE_WAIT_MS = 500;
/** R4.2: the heights flatten over the last 40% of the flight. */
export const EDIT_FLATTEN_FROM = 0.6;

export type EditFlightPhase =
  | 'idle'
  | 'reading'
  | 'flying'
  | 'landed'
  | 'navigated'
  | 'cancelled'
  | 'fallback';

/** R8: what one press came to, as logged. */
export type EditFlightOutcome =
  | 'flew'
  | 'no-plan'
  | 'plan-unread'
  | 'plan-timeout'
  | 'no-floor-match'
  | 'cancelled'
  | 'capture-failed'
  /** Something threw between the read and the landing: the scene is restored and Edit navigates. */
  | 'flight-failed';

export interface EditFlightState {
  phase: EditFlightPhase;
  /** One per press: an answer asked under an older one is dropped. */
  generation: number;
  outcome: EditFlightOutcome | null;
  /** The landing floor matched a live floor (R4.4: when not, everything flattens and none fades). */
  floorMatched: boolean;
}

export type EditFlightEvent =
  | { type: 'press' }
  | { type: 'planRead'; generation: number; target: 'target' | 'no-plan'; floorMatched: boolean }
  | { type: 'planFailed'; generation: number }
  | { type: 'planTimeout'; generation: number }
  /** The flight cannot run: nothing to fly from on the press, or a throw after the read. */
  | { type: 'fail'; generation: number; outcome: 'no-plan' | 'plan-unread' | 'flight-failed' }
  | { type: 'landed'; generation: number }
  | { type: 'captured'; generation: number; ok: boolean }
  | { type: 'cancel' }
  | { type: 'reset' };

export const initialEditFlight: EditFlightState = {
  phase: 'idle',
  generation: 0,
  outcome: null,
  floorMatched: false,
};

/** The phases a flight is under way in: a press then does nothing (R3.4), and a cancel stops it (R9). */
export const editFlightUnderWay = (phase: EditFlightPhase): boolean =>
  phase === 'reading' || phase === 'flying' || phase === 'landed';

const current = (state: EditFlightState, generation: number) =>
  generation === state.generation && editFlightUnderWay(state.phase);

export function editFlightReducer(state: EditFlightState, event: EditFlightEvent): EditFlightState {
  switch (event.type) {
    case 'press':
      if (editFlightUnderWay(state.phase)) return state;
      return {
        phase: 'reading',
        generation: state.generation + 1,
        outcome: null,
        floorMatched: false,
      };
    case 'planRead':
      if (!current(state, event.generation) || state.phase !== 'reading') return state;
      if (event.target === 'no-plan') return { ...state, phase: 'fallback', outcome: 'no-plan' };
      return { ...state, phase: 'flying', floorMatched: event.floorMatched };
    case 'planFailed':
      if (!current(state, event.generation) || state.phase !== 'reading') return state;
      return { ...state, phase: 'fallback', outcome: 'plan-unread' };
    case 'planTimeout':
      if (!current(state, event.generation) || state.phase !== 'reading') return state;
      return { ...state, phase: 'fallback', outcome: 'plan-timeout' };
    case 'fail':
      if (!current(state, event.generation)) return state;
      return { ...state, phase: 'fallback', outcome: event.outcome };
    case 'landed':
      if (!current(state, event.generation) || state.phase !== 'flying') return state;
      return { ...state, phase: 'landed' };
    case 'captured':
      if (!current(state, event.generation) || state.phase !== 'landed') return state;
      return {
        ...state,
        phase: 'navigated',
        outcome: !event.ok ? 'capture-failed' : state.floorMatched ? 'flew' : 'no-floor-match',
      };
    case 'cancel':
      if (!editFlightUnderWay(state.phase)) return state;
      // A new generation, so a late read, flight or capture answer does nothing.
      return {
        ...state,
        phase: 'cancelled',
        generation: state.generation + 1,
        outcome: 'cancelled',
      };
    case 'reset':
      return editFlightUnderWay(state.phase) ? state : { ...state, phase: 'idle', outcome: null };
    default:
      return state;
  }
}

/**
 * R4.2: the live twin's height scale at `progress` through the flight (0 to 1): full until 60%, then
 * eased to the plan view's rest scale, which it lands on with the camera.
 */
export const editFlightHeightAt = (progress: number): number => {
  if (!(progress > EDIT_FLATTEN_FROM)) return 1;
  const t = Math.min(1, (progress - EDIT_FLATTEN_FROM) / (1 - EDIT_FLATTEN_FROM));
  return 1 + (PLAN_REST_HEIGHT_SCALE - 1) * planEase(t);
};

/**
 * The flight's length as flown: `EDIT_FLIGHT_MS` slowed by `?slowFlight=N`, by the same rule
 * `flyToTarget` slows every flight (any finite N above 0, in any build). The camera is given
 * `EDIT_FLIGHT_MS` and slows itself; the height ease reads this, so the two share one clock.
 */
export const editFlightDuration = (search: string): number => {
  const raw = new URLSearchParams(search).get('slowFlight');
  if (!raw) return EDIT_FLIGHT_MS;
  const slow = Number(raw);
  return Number.isFinite(slow) && slow > 0 ? EDIT_FLIGHT_MS * slow : EDIT_FLIGHT_MS;
};

/**
 * R9 and every fallback: the scene as a customer sees it, the map's input back, the camera stopped.
 * A cancel restores full height (criterion 13.6).
 */
export const restoreLiveView = (map: MapboxMap | null, unlock: (() => void) | null): void => {
  resetLiveScene();
  // A map being removed can throw from either; the caller's reset and navigation still run.
  try {
    unlock?.();
  } catch {
    // Its input goes with it.
  }
  try {
    map?.stop();
  } catch {
    // Nothing left to stop.
  }
};
